import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { initDocLang } from '../lib/docLang';
import { Check, X, Trash2, UserCheck, UserX, Users, Key, Eye, EyeOff, Link, Unlink, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { SortableHeader, useSort, ResetViewButton } from './ui/SortableHeader';
import { dateLocale } from '../lib/dateFormat';
import { DateText } from './ui/week-badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Password reset state
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Employee linking state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [userToLink, setUserToLink] = useState(null);
  const [unlinkedEmployees, setUnlinkedEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [userEmployeeLinks, setUserEmployeeLinks] = useState({});
  const [linking, setLinking] = useState(false);

  // Profile edit state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [profileUsername, setProfileUsername] = useState('');
  const [profileFullName, setProfileFullName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Sort state for the All Users table
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('username', 'asc', 'users-list');
  const isViewCustomized = sortKey !== 'username' || sortDir !== 'asc';

  // Dynamic role registry — pulls system + custom roles from the
  // permissions matrix so any role created under Settings → Roles &
  // Permissions becomes assignable here automatically.
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchUnlinkedEmployees();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const r = await axios.get(`${API}/roles`);
      // Hide superadmin from the role dropdown — promotion is a
      // separate, gated action via PUT /users/{id}/superadmin.
      const list = (r.data || []).filter((row) => row.role !== 'superadmin');
      // Stable sort: system roles first (admin, manager, …), then
      // custom roles alphabetically.
      list.sort((a, b) => {
        if ((a.is_system ? 0 : 1) !== (b.is_system ? 0 : 1)) {
          return a.is_system ? -1 : 1;
        }
        return (a.label || a.role).localeCompare(b.label || b.role);
      });
      setRoles(list);
    } catch (e) {
      console.error('Failed to load roles', e);
      // Fall back to the legacy hardcoded set so the dropdown is never empty
      setRoles([
        { role: 'user', label: t('auto.lbl.user'), is_system: true },
        { role: 'employee', label: t('auto.lbl.employee'), is_system: true },
        { role: 'manager', label: t('auto.lbl.manager'), is_system: true },
        { role: 'admin', label: t('auto.lbl.admin'), is_system: true },
      ]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      
      // Fetch employee links for all users
      const links = {};
      for (const user of response.data) {
        try {
          const linkRes = await axios.get(`${API}/users/${user.id}/employee-link`);
          links[user.id] = linkRes.data;
        } catch {
          links[user.id] = { linked: false, employee: null };
        }
      }
      setUserEmployeeLinks(links);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('auto.proj.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUnlinkedEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/unlinked`);
      setUnlinkedEmployees(response.data);
    } catch (error) {
      console.error('Error fetching unlinked employees:', error);
    }
  };

  const handleApproval = async (userId, approve) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, { is_approved: approve });
      toast.success(approve ? t('auto.proj.userApprovedSuccessfully') : t('auto.proj.userApprovalRevoked'));
      fetchUsers();
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error(error.response?.data?.detail || 'Error updating user approval');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success(t('auto.proj.userRoleUpdatedSuccessfully'));
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Error updating user role');
    }
  };

  const handleLanguageChange = async (userId, newLang) => {
    try {
      await axios.put(`${API}/users/${userId}/language`, { language: newLang });
      toast.success(t('auto.proj.languageUpdated'));
      // If the admin just changed their OWN language, flip the running
      // i18n locale so the surrounding UI updates immediately without
      // needing a hard refresh.
      if (currentUser && userId === currentUser.id) {
        i18n.changeLanguage(newLang);
      }
      fetchUsers();
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error(error.response?.data?.detail || 'Error updating language');
    }
  };

  const handleDocLangChoice = async (userId, enabled) => {
    try {
      await axios.put(`${API}/users/${userId}/doc-language-choice`, { enabled });
      toast.success(t('docLang.saved'));
      if (currentUser && userId === currentUser.id) initDocLang({ ...currentUser, doc_language_choice: enabled });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('docLang.saveError'));
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success(t('auto.proj.userDeletedSuccessfully'));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const openPasswordReset = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setShowPassword(false);
    setPasswordResetDialogOpen(true);
  };

  const openProfileEdit = (user) => {
    setUserToEdit(user);
    setProfileUsername(user.username || '');
    setProfileFullName(user.full_name || '');
    setProfileEmail(user.email || '');
    setProfileDialogOpen(true);
  };

  const handleProfileSave = async () => {
    if (!userToEdit) return;
    const nextUsername = profileUsername.trim();
    const nextFullName = profileFullName.trim();
    const nextEmail = profileEmail.trim().toLowerCase();
    if (!nextFullName) {
      toast.error(t('auto.proj.fullNameCannotBeEmpty'));
      return;
    }
    if (!nextUsername) {
      toast.error(t('auto.proj.usernameCannotBeEmpty'));
      return;
    }
    if (nextUsername.length < 3) {
      toast.error(t('auto.proj.usernameMustBeAtLeast3'));
      return;
    }
    const payload = {};
    if (nextFullName !== (userToEdit.full_name || '')) payload.full_name = nextFullName;
    if (nextEmail !== (userToEdit.email || '').toLowerCase()) payload.email = nextEmail;
    if (nextUsername !== (userToEdit.username || '')) payload.username = nextUsername;
    if (Object.keys(payload).length === 0) {
      toast.info(t('auto.proj.noChangesToSave'));
      setProfileDialogOpen(false);
      return;
    }
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/users/${userToEdit.id}/profile`, payload);
      setUsers(prev => prev.map(u => (u.id === userToEdit.id ? { ...u, ...res.data } : u)));
      toast.success(`Profile updated for ${res.data.username}`);
      setProfileDialogOpen(false);
      setUserToEdit(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!userToResetPassword || !newPassword) return;
    
    if (newPassword.length < 8) {
      toast.error(t('auto.proj.passwordMustBeAtLeast8'));
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.put(`${API}/users/${userToResetPassword.id}/password`, { 
        new_password: newPassword 
      });
      toast.success(`Password reset successfully for ${userToResetPassword.username}`);
      setPasswordResetDialogOpen(false);
      setUserToResetPassword(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Error resetting password');
    } finally {
      setResettingPassword(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  // Employee linking functions
  const openLinkDialog = (user) => {
    setUserToLink(user);
    setSelectedEmployeeId('');
    fetchUnlinkedEmployees();
    setLinkDialogOpen(true);
  };

  const handleLinkEmployee = async () => {
    if (!userToLink || !selectedEmployeeId) return;
    
    setLinking(true);
    try {
      await axios.put(`${API}/users/${userToLink.id}/link-employee`, {
        employee_id: selectedEmployeeId
      });
      toast.success(t('auto.proj.userLinkedToEmployeeSuccessfully'));
      setLinkDialogOpen(false);
      setUserToLink(null);
      setSelectedEmployeeId('');
      fetchUsers();
      fetchUnlinkedEmployees();
    } catch (error) {
      console.error('Error linking employee:', error);
      toast.error(error.response?.data?.detail || 'Error linking employee');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkEmployee = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}/unlink-employee`);
      toast.success(t('auto.proj.userUnlinkedFromEmployee'));
      fetchUsers();
      fetchUnlinkedEmployees();
    } catch (error) {
      console.error('Error unlinking employee:', error);
      toast.error(error.response?.data?.detail || 'Error unlinking employee');
    }
  };

  const getEmployeeLinkBadge = (userId) => {
    const linkData = userEmployeeLinks[userId];
    if (linkData?.linked && linkData.employee) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          {linkData.employee.first_name} {linkData.employee.last_name}
        </Badge>
      );
    }
    return <Badge className="bg-gray-100 text-gray-600">{t('auto.proj.notLinked')}</Badge>;
  };

  const getRoleBadge = (role) => {
    // Known color hints for built-in roles; fall back to neutral grey
    // for custom roles so any new auditor / contractor role still
    // renders cleanly.
    const ROLE_COLORS = {
      superadmin: 'bg-purple-600',
      admin: 'bg-red-500',
      manager: 'bg-blue-500',
      accounts: 'bg-amber-500',
      employee: 'bg-green-500',
      auditor: 'bg-indigo-500',
      user: 'bg-gray-500',
    };
    // Prefer the human label from the role registry; fall back to the
    // raw role id with the underscore replaced for readability.
    const registry = (roles || []).find((r) => r.role === role);
    const label = registry?.label || (role ? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown');
    const color = ROLE_COLORS[role] || 'bg-slate-600';
    return <Badge className={`${color} text-white`}>{label}</Badge>;
  };

  const getApprovalBadge = (isApproved) => {
    if (isApproved) {
      return <Badge className="bg-green-100 text-green-800">{t('auto.proj.approved')}</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800">{t('auto.proj.pending')}</Badge>;
  };

  const pendingUsers = users.filter(u => !u.is_approved);

  if (loading) {
    return <div className="p-8 text-center">{t('auto.proj.loadingUsers')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Pending Approvals Section */}
      {pendingUsers.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-yellow-600" />
              <CardTitle className="text-lg">Pending Approvals ({pendingUsers.length})</CardTitle>
            </div>
            <CardDescription>{t('auto.proj.newUsersWaitingForAccountApproval')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auto.proj.username')}</TableHead>
                  <TableHead>{t('auto.proj.fullName')}</TableHead>
                  <TableHead>{t('auto.proj.email')}</TableHead>
                  <TableHead>{t('auto.proj.created')}</TableHead>
                  <TableHead className="text-right">{t('auto.proj.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{<DateText value={user.created_at} />}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                          onClick={() => handleApproval(user.id, true)}
                        >
                          <Check className="w-4 h-4 mr-1" /> {t('auto.proj.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => confirmDelete(user)}
                        >
                          <X className="w-4 h-4 mr-1" /> {t('auto.proj.reject')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Users Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <CardTitle className="text-lg">All Users ({users.length})</CardTitle>
            </div>
            <ResetViewButton show={isViewCustomized} onReset={resetSort} testId="reset-view-users" />
          </div>
          <CardDescription>{t('auto.proj.manageUserRolesPermissionsPasswordsAnd')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader column="username" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.proj.username')}</SortableHeader>
                <SortableHeader column="full_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.proj.fullName')}</SortableHeader>
                <SortableHeader column="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.proj.email')}</SortableHeader>
                <SortableHeader column="is_approved" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.proj.status')}</SortableHeader>
                <TableHead>{t('auto.proj.employeeLink')}</TableHead>
                <SortableHeader column="role" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.proj.role')}</SortableHeader>
                <TableHead>{t('auto.proj.language')}</TableHead>
                <TableHead title={t('docLang.columnHint')}>{t('docLang.column')}</TableHead>
                <TableHead className="text-right">{t('auto.proj.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...users].sort(comparator((u, key) => {
                switch (key) {
                  case 'username': return (u.username || '').toLowerCase();
                  case 'full_name': return (u.full_name || '').toLowerCase();
                  case 'email': return (u.email || '').toLowerCase();
                  case 'is_approved': return u.is_approved ? 1 : 0;
                  case 'role': return u.role || '';
                  default: return '';
                }
              })).map(user => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getApprovalBadge(user.is_approved)}</TableCell>
                  <TableCell>{getEmployeeLinkBadge(user.id)}</TableCell>
                  <TableCell>
                    <Select
                      value={user.role}
                      onValueChange={(value) => handleRoleChange(user.id, value)}
                    >
                      <SelectTrigger className="w-[150px]" data-testid={`user-role-trigger-${user.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r.role} value={r.role} data-testid={`role-option-${r.role}`}>
                            {r.label || r.role}
                            {!r.is_system && (
                              <span className="ml-1 text-[10px] text-slate-400">{t('auto.proj.custom')}</span>
                            )}
                          </SelectItem>
                        ))}
                        {/* Safety: if the user's current role is somehow
                            not in the registry, still render it so the
                            select doesn't show a blank value. */}
                        {user.role && !roles.some((r) => r.role === user.role) && (
                          <SelectItem value={user.role}>{user.role}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.preferred_language || 'en'}
                      onValueChange={(value) => handleLanguageChange(user.id, value)}
                    >
                      <SelectTrigger className="w-[110px]" data-testid={`user-language-trigger-${user.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en" data-testid={`user-language-option-en-${user.id}`}>English</SelectItem>
                        <SelectItem value="pl" data-testid={`user-language-option-pl-${user.id}`}>Polski</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!user.doc_language_choice}
                      onCheckedChange={(v) => handleDocLangChoice(user.id, v)}
                      aria-label={t('docLang.column')}
                      data-testid={`user-doc-lang-choice-${user.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* Edit Profile Button (admin full-name/email override) */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                        onClick={() => openProfileEdit(user)}
                        title={t('auto.proj.editFullNameEmail')}
                        data-testid={`edit-profile-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>

                      {/* Employee Link/Unlink Button */}
                      {userEmployeeLinks[user.id]?.linked ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => handleUnlinkEmployee(user.id)}
                          title={t('auto.proj.unlinkFromEmployee')}
                        >
                          <Unlink className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => openLinkDialog(user)}
                          title={t('auto.proj.linkToEmployee')}
                        >
                          <Link className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Password Reset Button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => openPasswordReset(user)}
                        title={t('auto.proj.resetPassword')}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      
                      {/* Approval Toggle */}
                      {user.is_approved ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          onClick={() => handleApproval(user.id, false)}
                          title={t('auto.proj.revokeApproval')}
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleApproval(user.id, true)}
                          title={t('auto.proj.approveUser')}
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Delete Button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => confirmDelete(user)}
                        title={t('auto.proj.deleteUser')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auto.proj.deleteUser2')}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the user &quot;{userToDelete?.username}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)}>{t('auto.proj.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              {t('auto.proj.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.proj.resetPassword2')}</DialogTitle>
            <DialogDescription>
              Set a new password for user &quot;{userToResetPassword?.username}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('auto.proj.newPassword')}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auto.proj.enterNewPassword')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateRandomPassword}
                 data-testid="btn-generate">
                  {t('auto.proj.generate')}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {t('auto.proj.passwordMustBeAtLeast8')}
              </p>
            </div>
            
            {newPassword && showPassword && (
              <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>{t('auto.proj.newPassword2')}</strong> {newPassword}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {t('auto.proj.makeSureToShareThisPassword')}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordResetDialogOpen(false);
                setUserToResetPassword(null);
                setNewPassword('');
              }}
             data-testid="dialog-cancel-btn">
              {t('auto.proj.cancel')}
            </Button>
            <Button
              onClick={handlePasswordReset}
              disabled={!newPassword || newPassword.length < 8 || resettingPassword}
             data-testid="dialog-passwordreset-btn">
              {resettingPassword ? t('auto.proj.resetting') : t('auto.proj.resetPassword2')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Employee Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.proj.linkUserToEmployee')}</DialogTitle>
            <DialogDescription>
              Link &quot;{userToLink?.username}&quot; to an employee profile. This will give them access to timesheets, expenses, and other employee features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="employee-select">{t('auto.proj.selectEmployeeProfile')}</Label>
              <Select
                value={selectedEmployeeId}
                onValueChange={setSelectedEmployeeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.proj.selectAnEmployee')} />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedEmployees.length === 0 ? (
                    <SelectItem value="none" disabled>{t('auto.proj.noUnlinkedEmployeesAvailable')}</SelectItem>
                  ) : (
                    unlinkedEmployees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} {emp.email ? `(${emp.email})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {t('auto.proj.onlyEmployeesNotAlreadyLinkedTo')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLinkDialogOpen(false);
                setUserToLink(null);
                setSelectedEmployeeId('');
              }}
             data-testid="dialog-cancel-btn">
              {t('auto.proj.cancel')}
            </Button>
            <Button
              onClick={handleLinkEmployee}
              disabled={!selectedEmployeeId || linking}
             data-testid="dialog-linkemployee-btn">
              {linking ? t('auto.proj.linking') : t('auto.proj.linkEmployee')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.proj.editUserProfile')}</DialogTitle>
            <DialogDescription>
              Admin override for user &quot;{userToEdit?.username}&quot;. Use this to
              correct a misconfigured full name or email on behalf of the user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="profile-username">{t('auto.proj.username')}</Label>
              <Input
                id="profile-username"
                value={profileUsername}
                onChange={(e) => setProfileUsername(e.target.value)}
                placeholder="e.g. jkowalski"
                data-testid="profile-username-input"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">
                {t('auto.proj.usedToSignInChangesAre')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-full-name">{t('auto.proj.fullName3')}</Label>
              <Input
                id="profile-full-name"
                value={profileFullName}
                onChange={(e) => setProfileFullName(e.target.value)}
                placeholder="e.g. Jan Kowalski"
                data-testid="profile-full-name-input"
              />
              <p className="text-xs text-gray-500">
                {t('auto.proj.thisNameAppearsOnReportsAnd')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email">{t('auto.proj.email')}</Label>
              <Input
                id="profile-email"
                type="email"
                value={profileEmail}
                onChange={(e) => setProfileEmail(e.target.value)}
                placeholder="name@company.com"
                data-testid="profile-email-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setProfileDialogOpen(false);
                setUserToEdit(null);
              }}
              disabled={savingProfile}
             data-testid="dialog-cancel-btn">
              {t('auto.proj.cancel')}
            </Button>
            <Button
              onClick={handleProfileSave}
              disabled={savingProfile}
              data-testid="profile-save-btn"
            >
              {savingProfile ? t('auto.proj.saving') : t('auto.proj.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
